import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import {
  register,
  resendVerificationCode,
  resetUserEmailResend,
  resetUserEmailVerify,
  resetUserRegister,
  verifyEmail,
} from "../../actions/userActions";
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_REQUIREMENTS_MESSAGE,
  buildClientRegistrationError,
} from "../../utils/registrationErrors";
import "./AuthScreen.css";

const TITLE_OPTIONS = ["", "Mr", "Mrs", "Ms", "Miss", "Dr", "Prof"];

function cleanText(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim();
}

function validateEmail(value = "") {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function errorText(error = "") {
  if (!error) return "";
  if (typeof error === "string") return error;
  return error.message || "";
}

function getVerificationEmailFromLocation(location) {
  const stateEmail = cleanText(location.state?.email);
  const queryEmail = cleanText(new URLSearchParams(location.search).get("email"));

  return stateEmail || queryEmail;
}

export default function RegisterScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [title, setTitle] = useState("");
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [verificationEmail, setVerificationEmail] = useState(() =>
    getVerificationEmailFromLocation(location),
  );
  const [verificationCode, setVerificationCode] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [localFormError, setLocalFormError] = useState("");
  const [localFieldErrors, setLocalFieldErrors] = useState({});

  const registerState = useSelector((state) => state.userRegister || {});
  const emailVerifyState = useSelector((state) => state.userEmailVerify || {});
  const emailResendState = useSelector((state) => state.userEmailResend || {});
  const userAuth = useSelector((state) => state.userAuth || {});

  const {
    loading,
    error,
    success,
    verificationRequired,
    email: registeredEmail = "",
    message: registerMessage = "",
  } = registerState;
  const {
    loading: verifying = false,
    error: verifyError,
    message: verifyMessage = "",
  } = emailVerifyState;
  const {
    loading: resending = false,
    error: resendError,
    message: resendMessage = "",
  } = emailResendState;

  const {
    userInfo = null,
    isAuthenticated = false,
    loading: authLoading = false,
  } = userAuth;

  const userId = userInfo?.id || userInfo?._id;
  const registerError =
    error && typeof error === "object"
      ? error
      : error
        ? { message: error, scope: "form", fieldErrors: {} }
        : null;
  const registerErrorIsFieldLevel = registerError?.scope === "field";
  const fieldErrors = {
    ...(registerError?.fieldErrors || {}),
    ...localFieldErrors,
  };
  const formError =
    localFormError ||
    (!registerErrorIsFieldLevel ? errorText(registerError) : "") ||
    errorText(verifyError) ||
    errorText(resendError);
  const isVerificationRoute = location.pathname === "/verify-email";
  const showVerificationForm =
    isVerificationRoute ||
    verificationRequired || Boolean(registerError?.verificationRequired);
  const successMessage =
    verifyMessage ||
    resendMessage ||
    registerMessage ||
    (showVerificationForm && !formError
      ? "Enter your email address and verification code, or request a new verification email."
      : "");

  const redirect = useMemo(() => {
    const queryRedirect =
      new URLSearchParams(location.search).get("redirect") || "";

    if (queryRedirect) {
      return queryRedirect.startsWith("/") ? queryRedirect : "/";
    }

    const from = location.state?.from;

    if (typeof from === "string") {
      return from.startsWith("/") ? from : "/";
    }

    if (from?.pathname) {
      return `${from.pathname || "/"}${from.search || ""}${from.hash || ""}`;
    }

    return "/";
  }, [location.search, location.state]);

  useEffect(() => {
    if ((success || isAuthenticated) && userId) {
      navigate(redirect, { replace: true });
    }
  }, [success, isAuthenticated, userId, navigate, redirect]);

  useEffect(() => {
    const pendingEmail =
      getVerificationEmailFromLocation(location) ||
      registeredEmail ||
      registerError?.email ||
      "";

    if (pendingEmail) {
      setVerificationEmail(pendingEmail);
    }
  }, [location, registeredEmail, registerError?.email]);

  useEffect(() => {
    return () => {
      dispatch(resetUserRegister());
      dispatch(resetUserEmailVerify());
      dispatch(resetUserEmailResend());
    };
  }, [dispatch]);

  const setClientError = (code, overrides = {}) => {
    const payload = buildClientRegistrationError(code, overrides);

    setLocalFieldErrors(payload.fieldErrors || {});
    setLocalFormError(payload.scope === "field" ? "" : payload.message);

    return payload;
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    setLocalFormError("");
    setLocalFieldErrors({});

    const cleanedTitle = cleanText(title);
    const cleanedName = cleanText(name);
    const cleanedLastName = cleanText(lastName);
    const trimmedEmail = email.trim();

    if (!TITLE_OPTIONS.includes(cleanedTitle)) {
      setClientError("INVALID_REGISTRATION_DATA", {
        field: "title",
        fieldErrors: { title: "Choose a supported title." },
      });
      return;
    }

    if (!cleanedName) {
      setClientError("NAME_REQUIRED", { field: "name" });
      return;
    }

    if (cleanedName.length < 2) {
      setClientError("INVALID_REGISTRATION_DATA", {
        field: "name",
        fieldErrors: { name: "First name must be between 2 and 100 characters." },
      });
      return;
    }

    if (cleanedName.length > 100) {
      setClientError("INVALID_REGISTRATION_DATA", {
        field: "name",
        fieldErrors: { name: "First name must be between 2 and 100 characters." },
      });
      return;
    }

    if (!cleanedLastName) {
      setClientError("NAME_REQUIRED", { field: "lastName" });
      return;
    }

    if (cleanedLastName.length > 100) {
      setClientError("INVALID_REGISTRATION_DATA", {
        field: "lastName",
        fieldErrors: {
          lastName: "Last name must be between 1 and 100 characters.",
        },
      });
      return;
    }

    if (!trimmedEmail) {
      setClientError("EMAIL_REQUIRED");
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setClientError("INVALID_EMAIL");
      return;
    }

    if (!password) {
      setClientError("PASSWORD_REQUIRED");
      return;
    }

    if (password.length < PASSWORD_MIN_LENGTH) {
      setClientError("PASSWORD_TOO_SHORT");
      return;
    }

    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(password)) {
      setClientError("PASSWORD_REQUIREMENTS", {
        fieldErrors: { password: PASSWORD_REQUIREMENTS_MESSAGE },
      });
      return;
    }

    if (password !== confirmPassword) {
      setClientError("PASSWORDS_DO_NOT_MATCH");
      return;
    }

    const result = await dispatch(
      register({
        title: cleanedTitle,
        name: cleanedName,
        lastName: cleanedLastName,
        email: trimmedEmail,
        password,
      }),
    );

    if (result?.verificationRequired) {
      const pendingEmail = result.email || trimmedEmail;

      setVerificationEmail(pendingEmail);
      navigate("/verify-email", {
        state: { email: pendingEmail, from: redirect },
      });
    }
  };

  const verifyHandler = async (e) => {
    e.preventDefault();
    setLocalFormError("");
    setLocalFieldErrors({});

    const trimmedEmail = verificationEmail.trim() || email.trim();
    const cleanedCode = verificationCode.trim();

    if (!trimmedEmail || !cleanedCode) {
      setLocalFormError("Enter your email address and verification code.");
      return;
    }

    const result = await dispatch(verifyEmail(trimmedEmail, cleanedCode));

    if (result?.user) {
      setVerificationEmail("");
      setVerificationCode("");
      dispatch(resetUserRegister());
      dispatch(resetUserEmailVerify());
      dispatch(resetUserEmailResend());
    }
  };

  const resendHandler = () => {
    setLocalFormError("");
    setLocalFieldErrors({});
    const trimmedEmail = verificationEmail.trim() || email.trim();

    if (!trimmedEmail) {
      setLocalFormError("Enter your email address first.");
      return;
    }

    dispatch(resendVerificationCode(trimmedEmail));
  };

  const resetVerificationFlow = () => {
    setVerificationEmail("");
    setVerificationCode("");
    dispatch(resetUserRegister());
    dispatch(resetUserEmailVerify());
    dispatch(resetUserEmailResend());
  };

  return (
    <section className="auth-shell">
      <div className="auth-card">
        <div className="auth-grid">
          <div className="auth-panel auth-panel--form">
            <h1 className="auth-title">
              {isVerificationRoute ? "Verify Email" : "Create Account"}
            </h1>

            {formError && <div className="auth-alert">{formError}</div>}

            {successMessage && (
              <div className="auth-success">{successMessage}</div>
            )}

            {!isVerificationRoute && (
            <form className="auth-form" onSubmit={submitHandler} noValidate>
              <div className="auth-field">
                <label htmlFor="title">Title</label>
                <select
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  autoComplete="honorific-prefix"
                  aria-invalid={Boolean(fieldErrors.title)}
                  aria-describedby={
                    fieldErrors.title ? "title-error" : undefined
                  }
                >
                  <option value="">Prefer not to say</option>
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Ms">Ms</option>
                  <option value="Miss">Miss</option>
                  <option value="Dr">Dr</option>
                  <option value="Prof">Prof</option>
                </select>
                {fieldErrors.title && (
                  <p id="title-error" className="auth-field-error">
                    {fieldErrors.title}
                  </p>
                )}
              </div>

              <div className="auth-field">
                <label htmlFor="name">First name*</label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="given-name"
                  minLength={2}
                  maxLength={100}
                  aria-invalid={Boolean(fieldErrors.name)}
                  aria-describedby={fieldErrors.name ? "name-error" : undefined}
                  required
                />
                {fieldErrors.name && (
                  <p id="name-error" className="auth-field-error">
                    {fieldErrors.name}
                  </p>
                )}
              </div>

              <div className="auth-field">
                <label htmlFor="lastName">Last name*</label>
                <input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                  maxLength={100}
                  aria-invalid={Boolean(fieldErrors.lastName)}
                  aria-describedby={
                    fieldErrors.lastName ? "lastName-error" : undefined
                  }
                  required
                />
                {fieldErrors.lastName && (
                  <p id="lastName-error" className="auth-field-error">
                    {fieldErrors.lastName}
                  </p>
                )}
              </div>

              <div className="auth-field">
                <label htmlFor="email">Email address*</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  aria-invalid={Boolean(fieldErrors.email)}
                  aria-describedby={
                    fieldErrors.email ? "email-error" : undefined
                  }
                  required
                />
                {fieldErrors.email && (
                  <p id="email-error" className="auth-field-error">
                    {fieldErrors.email}
                  </p>
                )}
              </div>

              <div className="auth-field">
                <label htmlFor="password">Password*</label>
                <div className="auth-password-wrap">
                  <input
                    id="password"
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    aria-invalid={Boolean(fieldErrors.password)}
                    aria-describedby={
                      fieldErrors.password ? "password-error" : undefined
                    }
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                  />
                  <button
                    type="button"
                    className="auth-eye"
                    onClick={() => setShowPw((s) => !s)}
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
                {fieldErrors.password && (
                  <p id="password-error" className="auth-field-error">
                    {fieldErrors.password}
                  </p>
                )}
              </div>

              <div className="auth-field">
                <label htmlFor="confirmPassword">Confirm password*</label>
                <div className="auth-password-wrap">
                  <input
                    id="confirmPassword"
                    type={showConfirmPw ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    aria-invalid={Boolean(fieldErrors.confirmPassword)}
                    aria-describedby={
                      fieldErrors.confirmPassword
                        ? "confirmPassword-error"
                        : undefined
                    }
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                  />
                  <button
                    type="button"
                    className="auth-eye"
                    onClick={() => setShowConfirmPw((s) => !s)}
                    aria-label={
                      showConfirmPw ? "Hide password" : "Show password"
                    }
                  >
                    {showConfirmPw ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
                {fieldErrors.confirmPassword && (
                  <p id="confirmPassword-error" className="auth-field-error">
                    {fieldErrors.confirmPassword}
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="auth-btn auth-btn--primary auth-btn--gold"
                disabled={loading || authLoading}
              >
                {loading || authLoading
                  ? "Creating account..."
                  : "Create account"}
              </button>
            </form>
            )}

            {showVerificationForm && (
              <form className="auth-form" onSubmit={verifyHandler}>
                <div className="auth-field">
                  <label htmlFor="verificationEmail">Verification email</label>
                  <input
                    id="verificationEmail"
                    type="email"
                    value={verificationEmail}
                    onChange={(e) => setVerificationEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>

                <div className="auth-field">
                  <label htmlFor="verificationCode">Verification code</label>
                  <input
                    id="verificationCode"
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    autoComplete="one-time-code"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="auth-btn auth-btn--primary auth-btn--gold"
                  disabled={verifying}
                >
                  {verifying ? "Verifying..." : "Verify email"}
                </button>

                <button
                  type="button"
                  className="auth-btn auth-btn--secondary"
                  onClick={resendHandler}
                  disabled={resending}
                >
                  {resending ? "Sending..." : "Resend verification email"}
                </button>

                <Link
                  to="/register"
                  className="auth-inline-link"
                  onClick={resetVerificationFlow}
                >
                  Use a different email address
                </Link>

                <Link
                  to="/login"
                  className="auth-inline-link"
                  onClick={resetVerificationFlow}
                >
                  Return to sign in
                </Link>
              </form>
            )}
          </div>

          <div className="auth-panel auth-panel--info">
            <h2 className="auth-side-title">Already have an account?</h2>

            <p className="auth-copy">
              Sign in to manage orders and checkout faster.
            </p>

            <ul className="auth-benefits">
              <li>Saved addresses for quick checkout</li>
              <li>Order history and tracking</li>
              <li>Faster support</li>
            </ul>

            <Link to="/login" className="auth-btn auth-btn--secondary">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
